import { forwardRef, Ref, useEffect, useRef, useState } from 'react';
import {
	Box,
	CircularProgress,
	Grid2,
	Typography,
	IconButton,
	TextField,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import _, { throttle } from 'lodash';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import {
	ArrowDownIcon,
	BackArrowIcon,
	CloseIcon,
	PDFIcon,
	SendChatIcon,
	SendLinkIcon,
} from '../../assets/icons/Icons';
import SideNavigationBar from '../../components/SideNavigationBar';
import Avatar from '../../assets/images/Avatar.png';
import {
	downloadMediaFile,
	fetchChatNumbers,
	fetchChats,
	onEnableAgentMode,
	onFetchUploadInfo,
	onResetChatMsgCount,
	sendMessage,
	sendUploadIdAndMessage,
	uploadPdf,
} from '../../services/ApiService';
import {
	ChatData,
	ChatList,
	Chats,
	ChatsResponse,
	ErrorStatus,
	Tag,
} from '../../types';
import { convertTo12HourTime, dateFormatter } from '../../Utils/Helpers';
import ChatRow from '../../components/ChatRow';
import CustomSwitch from '../../components/CustomSwitch';
import { GREY, MAIN_THEME_COLOR, WHITE } from '../../assets/theme/Colors';
import { CHAT_SEARCH_TAGS } from '../../Utils/Enums';
import ChatNumbersList from './ChatNumbersList';
import { getAgencyId, getUserEmail, getUserId } from '../../Utils/AuthUtil';
import { getMsgReadStatus } from '../../context/MainProvider';
import { onAddClickStat } from '../../services/UserService';

interface ChatWindowProps {
	isChatView: boolean;
	chatData: { [index: string]: Array<ChatData> };
	loading: boolean;
	message: string;
	setMessage: (value: string) => void;
	isConsultantModeOn: boolean;
	onChangeConsultantMode: (
		event: React.ChangeEvent<HTMLInputElement>,
	) => void;
	onSendChat: () => void;
	messageError: ErrorStatus;
	isFileSelected: boolean;
	selectedFile: File | null;
	onCancelSelectedFile: () => void;
	onSelectAttachments: (event: React.ChangeEvent<HTMLInputElement>) => void;
	previewUrl: string;
	onDownloadFile: (url: string) => void;
	isNewMessageReceived: boolean;
	onScrollDown: () => void;
	isInitialFetchDone: boolean;
	handleScroll: () => void;
	isSending: boolean;
	isDownloading: boolean;
	downloadingFile: string;
}

interface ChatWindowMainProps {
	isChatWindow: boolean;
	selectedChatData: { [index: string]: Array<ChatData> };
	isChatLoading: boolean;
	isMobile: boolean;
	onclickGoBack: () => void;
	message: string;
	setMessage: (value: string) => void;
	isConsultantModeOn: boolean;
	onChangeConsultantMode: (
		event: React.ChangeEvent<HTMLInputElement>,
	) => void;
	selectedChat: SelectedChat;
	onSendChat: () => void;
	messageError: ErrorStatus;
	isFileSelected: boolean;
	selectedFile: File | null;
	onCancelSelectedFile: () => void;
	onSelectAttachments: (event: React.ChangeEvent<HTMLInputElement>) => void;
	previewUrl: string;
	onDownloadFile: (url: string) => void;
	isNewMessageReceived: boolean;
	onScrollDown: () => void;
	isInitialFetchDone: boolean;
	handleScroll: () => void;
	isSending: boolean;
	isDownloading: boolean;
	downloadingFile: string;
	packageName: string | null;
}

interface SelectedChat {
	phoneNumber: string;
	name: string;
	isConsultantNeeded: boolean;
	phoneNumberExtra: string;
	package: string | null;
}

const ERROR_INITIAL_STATES: ErrorStatus = {
	status: false,
	msg: '',
};

const ChatBodyWindow = forwardRef<HTMLDivElement, ChatWindowProps>(
	function ChatBodyWindow(
		{
			isChatView,
			chatData,
			loading,
			message,
			setMessage,
			isConsultantModeOn,
			onChangeConsultantMode,
			onSendChat,
			messageError,
			isFileSelected,
			selectedFile,
			onCancelSelectedFile,
			onSelectAttachments,
			previewUrl,
			onDownloadFile,
			isNewMessageReceived,
			onScrollDown,
			isInitialFetchDone,
			handleScroll,
			isSending,
			isDownloading,
			downloadingFile,
		}: ChatWindowProps,
		ref,
	) {
		if (isChatView && !loading) {
			return (
				<Grid2 width={'100%'} paddingX={'5px'}>
					<Box
						ref={ref}
						onScroll={handleScroll}
						sx={{
							overflowY: 'auto',
							height: isFileSelected ? '47vh' : '70vh',
							width: '100%',
							'&::-webkit-scrollbar': {
								display: 'none', // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
							},
							'&-ms-overflow-style:': {
								display: 'none', // Hide the scrollbar for IE
							},
						}}>
						<Grid2 width={'100%'} padding={'10px'}>
							{Object.keys(chatData).map((item, index) => {
								return (
									<Grid2 key={index}>
										<Typography
											variant="body2"
											fontWeight={'500'}
											textAlign={'center'}>
											{dateFormatter(item)}
										</Typography>
										{chatData[item].map((chat, value) => {
											return (
												<ChatRow
													key={value}
													position={
														chat.type === 'human'
															? 'left'
															: 'right'
													}
													text={chat.text}
													chatTime={chat.time}
													fileName={chat.fileName}
													fileUrl={chat.fileUrl}
													onDownload={onDownloadFile}
													caption={chat.caption}
													chatOwner={chat.type}
													isDownloading={
														isDownloading
													}
													downloadingFile={
														downloadingFile
													}
													status={chat.status}
												/>
											);
										})}
									</Grid2>
								);
							})}
						</Grid2>
					</Box>
					{messageError.status && (
						<Grid2 container>
							<Typography
								fontSize={'10px'}
								color={'red'}
								marginLeft={'20px'}>
								{messageError.msg}
							</Typography>
						</Grid2>
					)}
					{isInitialFetchDone && isNewMessageReceived && (
						<Grid2 container justifyContent={'center'}>
							<IconButton
								disableRipple
								disableFocusRipple
								sx={{
									padding: '5px',
									flexDirection: 'row',
									borderRadius: '5px',
									backgroundColor: MAIN_THEME_COLOR,
								}}
								onClick={onScrollDown}>
								<ArrowDownIcon />
								<Typography variant="caption" color={'#FFFFFF'}>
									New Message
								</Typography>
							</IconButton>
						</Grid2>
					)}
					{isFileSelected && previewUrl && (
						<Box
							sx={{
								position: 'relative',
								backgroundColor: '#F2F4F5',
								flexDirection: 'row',
								justifyContent: 'space-between',
								width: { md: '74.5%', xs: '100%' },
								marginRight: '20px',
								borderTopLeftRadius: '20px',
								borderTopRightRadius: '20px',
								borderBottomLeftRadius: '0px',
								borderBottomRightRadius: '0px',
							}}
							padding={'10px'}>
							<Grid2
								component={'div'}
								sx={{
									width: '90%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									flexDirection: 'column',
								}}>
								<img
									src={previewUrl}
									alt="Preview"
									style={{
										width: '95%',
										maxHeight: '120px',
										objectFit: 'contain',
									}}
								/>
								<Typography
									style={{
										wordWrap: 'break-word',
									}}
									color={'#292D32'}
									fontSize={'12px'}>
									{selectedFile?.name}
								</Typography>
								<Typography
									color={'#A9ACB4'}
									marginRight={'5px'}
									fontSize={'10px'}>
									{(
										(selectedFile?.size
											? selectedFile.size
											: 0) / 1024
									).toFixed(2)}{' '}
									KB
								</Typography>
							</Grid2>
							<IconButton
								sx={{
									position: 'absolute',
									top: '20px',
									right: '20px',
									padding: '0px',
								}}
								onClick={onCancelSelectedFile}>
								<CloseIcon />
							</IconButton>
						</Box>
					)}
					{isFileSelected && selectedFile && !previewUrl && (
						<Grid2
							container
							size={{ xs: 12, md: 9 }}
							sx={{
								backgroundColor: '#F2F4F5',
								justifyContent: 'space-between',
								width: { md: '74.5%', xs: '100%' },
								marginRight: '20px',
								borderTopLeftRadius: '20px',
								borderTopRightRadius: '20px',
								borderBottomLeftRadius: '0px',
								borderBottomRightRadius: '0px',
							}}
							padding={'20px'}>
							<Grid2
								component={'div'}
								sx={{
									width: '90%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									flexDirection: 'column',
								}}>
								<PDFIcon />
								<Typography
									style={{
										wordWrap: 'break-word',
									}}
									color={'#292D32'}
									fontSize={'12px'}>
									{selectedFile?.name}
								</Typography>
								<Typography
									color={'#A9ACB4'}
									marginRight={'5px'}
									fontSize={'10px'}>
									{(
										(selectedFile?.size
											? selectedFile.size
											: 0) / 1024
									).toFixed(2)}{' '}
									KB
								</Typography>
							</Grid2>
							<IconButton
								sx={{ padding: '0px', height: '10%' }}
								onClick={onCancelSelectedFile}>
								<CloseIcon />
							</IconButton>
						</Grid2>
					)}
					<Grid2
						container
						width={'100%'}
						spacing={2}
						alignItems={'flex-end'}>
						{isConsultantModeOn ? (
							<Grid2
								component={'div'}
								size={{ xs: 12, md: 9 }}
								sx={{
									display: 'flex',
									alignItems: 'center',
									marginY: isFileSelected ? '0px' : '5px',
									padding: '5px',
									bgcolor: '#F2F4F5',
									borderTopLeftRadius: isFileSelected
										? '0px'
										: '20px',
									borderTopRightRadius: isFileSelected
										? '0px'
										: '20px',
									borderBottomLeftRadius: '20px',
									borderBottomRightRadius: '20px',
								}}>
								<TextField
									type="text"
									variant="standard"
									multiline
									placeholder="Type your message here..."
									sx={{
										width: { md: '93%', xs: '82%' },
										'& fieldset': {
											border: 'none',
										},
									}}
									slotProps={{
										input: {
											style: {
												color: '#000000',
												paddingLeft: '5px',
												fontSize: '14px',
											},
											disableUnderline: true,
										},
									}}
									onKeyDown={event => {
										const isMobile =
											'ontouchstart' in window ||
											/Mobi|Android/i.test(
												navigator.userAgent,
											);
										if (event.key === 'Enter') {
											if (!event.shiftKey && !isMobile) {
												event.preventDefault();
												onSendChat();
											}
										}
									}}
									value={message}
									onChange={e => setMessage(e.target.value)}
								/>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignSelf: 'flex-end',
										alignItems: 'center',
										marginLeft: '8px',
									}}>
									<IconButton component="label">
										<SendLinkIcon />
										<input
											hidden
											type="file"
											onChange={onSelectAttachments}
										/>
									</IconButton>
									{isSending ? (
										<CircularProgress
											sx={{
												color: '#28696D',
												marginRight: '5px',
											}}
											size={20}
										/>
									) : (
										<IconButton
											disabled={
												isFileSelected
													? false
													: message === ''
														? true
														: false
											}
											onClick={onSendChat}>
											<SendChatIcon />
										</IconButton>
									)}
								</Box>
							</Grid2>
						) : (
							<Grid2
								component={'div'}
								size={{ xs: 12, md: 9 }}
								sx={{
									display: 'flex',
									alignItems: 'center',
									marginY: '5px',
									padding: '5px',
									bgcolor: '#FFFFFF',
								}}>
								<Grid2 height={'36px'} />
							</Grid2>
						)}
						<Grid2
							component={'div'}
							size={{ xs: 7, md: 3 }}
							sx={{
								border: `1px solid ${isConsultantModeOn ? MAIN_THEME_COLOR : GREY}`,
								borderRadius: '5px',
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								paddingLeft: '4px',
								height: 45,
								marginY: '5px',
								backgroundColor: isConsultantModeOn
									? '#DEECED'
									: WHITE,
								ml: 'auto',
							}}>
							<Typography
								fontSize={'14px'}
								color={
									isConsultantModeOn ? MAIN_THEME_COLOR : GREY
								}
								sx={{ marginRight: '5px' }}>
								Consultant Mode
							</Typography>
							<CustomSwitch
								isChecked={isConsultantModeOn}
								handleChange={onChangeConsultantMode}
							/>
						</Grid2>
					</Grid2>
				</Grid2>
			);
		} else if (!isChatView && loading) {
			return (
				<Grid2
					container
					width={'100%'}
					height={'70vh'}
					justifyContent={'center'}
					alignItems={'center'}>
					<CircularProgress sx={{ color: MAIN_THEME_COLOR }} />
				</Grid2>
			);
		} else {
			return (
				<Grid2
					container
					width={'100%'}
					height={'70vh'}
					justifyContent={'center'}
					alignItems={'center'}>
					<Typography fontSize={'16px'} fontWeight={'400'}>
						No chat data available
					</Typography>
				</Grid2>
			);
		}
	},
);

function ChatWindowMain(
	{
		isChatWindow,
		selectedChatData,
		isChatLoading,
		isMobile,
		onclickGoBack,
		message,
		setMessage,
		isConsultantModeOn,
		onChangeConsultantMode,
		selectedChat,
		onSendChat,
		messageError,
		isFileSelected,
		selectedFile,
		onCancelSelectedFile,
		onSelectAttachments,
		previewUrl,
		onDownloadFile,
		isNewMessageReceived,
		onScrollDown,
		isInitialFetchDone,
		handleScroll,
		isSending,
		isDownloading,
		downloadingFile,
		packageName,
	}: ChatWindowMainProps,
	messagesEndRef: Ref<HTMLDivElement>,
) {
	function isToPhoneNumberTooLong(number: string) {
		if (selectedChat.name != 'Unknown') {
			if (isMobile && number.length > 21) {
				return number.slice(0, 21) + '...';
			} else {
				return number;
			}
		}

		return '';
	}

	return (
		<Grid2 component="div" size={{ xs: 12, md: 8 }}>
			<Grid2
				bgcolor={'#FFFFFF'}
				borderRadius={'5px'}
				sx={{ height: '100%' }}>
				<Grid2
					container
					justifyContent={'space-between'}
					alignItems={'center'}
					padding={'10px'}>
					{selectedChat.phoneNumber !== '' && (
						<Grid2
							container
							alignItems={'center'}
							justifyContent={
								isMobile ? 'flex-start' : 'space-between'
							}
							width={'100%'}>
							{isMobile && (
								<IconButton onClick={onclickGoBack}>
									<BackArrowIcon />
								</IconButton>
							)}
							<Grid2 container>
								<Box
									component={'img'}
									src={Avatar}
									width={40}
									height={40}
								/>
								<Grid2
									component={'div'}
									flexDirection={'row'}
									paddingLeft="15px">
									<Typography
										fontWeight={'500'}
										color="#000000"
										fontSize={'14px'}>
										{selectedChat.name != 'Unknown'
											? selectedChat.name
											: selectedChat.phoneNumber}
									</Typography>
									<Typography
										fontWeight={'500'}
										color="#7c7c7c"
										fontSize={'12px'}>
										{isToPhoneNumberTooLong(
											selectedChat.phoneNumberExtra,
										)}
									</Typography>
								</Grid2>
							</Grid2>
							{packageName && (
								<Grid2
									container
									alignItems={'center'}
									justifyContent={'center'}
									marginLeft={isMobile ? '40px' : '0px'}
									marginTop={isMobile ? '10px' : '0px'}>
									<Typography
										fontWeight={'500'}
										color="#000000"
										fontSize={'12px'}
										marginRight={'5px'}>
										Package:
									</Typography>
									<Box
										sx={{
											display: 'inline-flex',
											alignItems: 'center',
											px: 1.5,
											py: 0.5,
											borderRadius: '10px',
											fontWeight: 500,
											backgroundColor: `${MAIN_THEME_COLOR}20`,
											color: MAIN_THEME_COLOR,
										}}>
										<Typography
											sx={{
												fontSize: '12px',
												fontWeight: 500,
												color: 'inherit',
											}}>
											{packageName}
										</Typography>
									</Box>
								</Grid2>
							)}
						</Grid2>
					)}
				</Grid2>
				<Grid2 container>
					<ChatBodyWindow
						isChatView={isChatWindow}
						chatData={selectedChatData}
						ref={messagesEndRef}
						loading={isChatLoading}
						message={message}
						setMessage={setMessage}
						isConsultantModeOn={isConsultantModeOn}
						onChangeConsultantMode={onChangeConsultantMode}
						onSendChat={onSendChat}
						messageError={messageError}
						isFileSelected={isFileSelected}
						selectedFile={selectedFile}
						onCancelSelectedFile={onCancelSelectedFile}
						onSelectAttachments={onSelectAttachments}
						previewUrl={previewUrl}
						onDownloadFile={onDownloadFile}
						isNewMessageReceived={isNewMessageReceived}
						onScrollDown={onScrollDown}
						isInitialFetchDone={isInitialFetchDone}
						handleScroll={handleScroll}
						isSending={isSending}
						isDownloading={isDownloading}
						downloadingFile={downloadingFile}
					/>
				</Grid2>
			</Grid2>
		</Grid2>
	);
}

const ForwardedChatWindowMain = forwardRef(ChatWindowMain);

function Conversations() {
	const mainDivStyle = useMainCustomStyle();
	const theme = useTheme();
	const userId = getUserId();
	const orgId = getAgencyId();
	const { fetchMsgDetails } = getMsgReadStatus();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const [selectedChat, setSelectedChat] = useState<SelectedChat>({
		phoneNumber: '',
		name: '',
		isConsultantNeeded: false,
		phoneNumberExtra: '',
		package: null,
	});
	const [isChatWindow, setIsChatWindow] = useState(false);
	const [selectedChatData, setSelectedChatData] = useState<{
		[index: string]: Array<ChatData>;
	}>({});
	const [chatListData, setChatListData] = useState<Array<ChatList>>([]);
	const [chatListAllData, setChatListAllData] = useState<Array<ChatList>>([]);
	const [loading, setLoading] = useState(false);
	const [isChatLoading, setIsChatLoading] = useState(false);
	const [showNumberList, setShowNumberList] = useState(true);
	const [message, setMessage] = useState('');
	const [enableConsultantMode, setEnableConsultantMode] = useState(false);
	const [tagList, setTagList] = useState<Array<Tag>>([]);
	const [selectedTag, setSelectedTag] = useState('All');
	const [messageError, setMessageError] =
		useState<ErrorStatus>(ERROR_INITIAL_STATES);
	const [isSending, setIsSending] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoadingChatFirstTime, setIsLoadingChatFirstTime] = useState(true);
	const [isFileSelected, setIsFileSelected] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const timeoutId = useRef<NodeJS.Timeout | null>(null);
	const numberListTimeoutId = useRef<NodeJS.Timeout | null>(null);
	const previousChatDataRef = useRef<{
		[index: string]: Array<ChatData>;
	}>({});
	const [isNewMessageReceived, setIsNewMessageReceived] = useState(false);
	const enableAutoScrollRef = useRef<boolean>(false);
	const isInitialFetchDoneRef = useRef<boolean>(false);
	const isNewMessageSendRef = useRef<boolean>(false);
	const isCurrentChatNumberRef = useRef<string>('');
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadingFile, setDownloadingFile] = useState('');
	const [assignedPackageName, setAssignedPackageName] = useState<
		string | null
	>(null);

	useEffect(() => {
		fetchChatNumbersList();
	}, []);

	useEffect(() => {
		const scrollToBottom = () => {
			if (messagesEndRef.current) {
				messagesEndRef.current.style.scrollBehavior = 'auto';
				messagesEndRef.current.scrollTop =
					messagesEndRef.current.scrollHeight;

				setTimeout(() => {
					if (messagesEndRef.current) {
						messagesEndRef.current.style.scrollBehavior = 'smooth';
					}
				}, 100);
			}
		};

		if (isLoadingChatFirstTime || enableAutoScrollRef.current) {
			scrollToBottom();
		}

		const handleResize = () => {
			setTimeout(scrollToBottom, 200); // delay for layout to settle in mobile
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [selectedChatData, enableAutoScrollRef.current]);

	//TODO: This is a short term fix for real-time update on chat conversation
	//Remove this useEffect once the actual solution is implemented
	useEffect(() => {
		return () => {
			if (timeoutId.current) {
				clearTimeout(timeoutId.current);
			}

			if (numberListTimeoutId.current) {
				clearTimeout(numberListTimeoutId.current);
			}
		};
	}, []);

	useEffect(() => {
		const chat = chatListData.find(
			item => item.phoneNumber === selectedChat.phoneNumber,
		);

		setSelectedChat(prevState => ({
			...prevState,
			name: chat?.name ? chat?.name : prevState.name,
			isConsultantNeeded: chat?.isConsultantNeeded
				? chat?.isConsultantNeeded
				: false,
		}));

		setEnableConsultantMode(
			chat?.isConsultantNeeded ? chat?.isConsultantNeeded : false,
		);
	}, [chatListData, selectedChatData]);

	useEffect(() => {
		if (searchTerm === '') {
			fetchChatNumbersList(false);
		}
	}, [searchTerm]);

	//TODO: This is a short term fix for real-time update on chat conversation
	//Once it's integrated with actual solution remove this code
	useEffect(() => {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}

		if (searchTerm === '') {
			//TODO: This is a short term fix for real-time update on chat conversation
			//Once it's integrated with actual solution remove this code
			numberListTimeoutId.current = setTimeout(
				() => fetchChatNumbersList(false),
				10000,
			);
		}

		return () => {
			if (numberListTimeoutId.current) {
				clearTimeout(numberListTimeoutId.current);
			}
		};
	}, [chatListData]);

	async function fetchChatNumbersList(enableLoading = true) {
		if (enableLoading) {
			setLoading(true);
		}
		const data: Array<ChatsResponse> = await fetchChatNumbers(
			userId ? userId : '',
			orgId ? orgId : '',
		);

		const uniqueDataById = _.uniqBy(data, 'phone_number');

		const latestChatData = _.orderBy(uniqueDataById, 'time_stamp', 'desc');

		const chatList: Array<ChatList> = latestChatData.map(item => {
			const timeStamps = item.time_stamp
				? `${item.time_stamp} | ${convertTo12HourTime(item.time_stamp?.split(' ')[1])}`
				: 'N/A | N/A';

			return {
				name: item.name,
				phoneNumber: item.phone_number,
				timestamp: timeStamps,
				isConsultantNeeded: item.consultant_mode ? true : false,
				unreadMessageCount:
					item.unread_count === 0 ||
					item.unread_count === '' ||
					isCurrentChatNumberRef.current === item.phone_number
						? 0
						: item.unread_count,
				package: item.shared_pkg_org,
			};
		});

		const newTagList: Array<Tag> = CHAT_SEARCH_TAGS.map(item => {
			const count =
				item === 'Escalated'
					? chatList.filter(val => val.isConsultantNeeded).length
					: chatList.length;
			return {
				name: item,
				count: count,
			};
		});

		if (selectedTag !== 'All') {
			const filteredData = chatList.filter(val => val.isConsultantNeeded);
			setChatListData(filteredData);
		} else {
			setChatListData(chatList);
		}

		setChatListAllData(chatList);
		setTagList(newTagList);
		setLoading(false);
	}

	async function onViewRecentChat(
		chat: SelectedChat,
		isViewClosed: boolean = true,
	) {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this if statement (do not remove the code inside the if statement just remove the statement)
		//Also remove isViewClosed parameter
		if (isViewClosed) {
			setIsChatWindow(false);
			setIsChatLoading(true);
			setSelectedChatData({});
			setIsNewMessageReceived(false);
			setSelectedChat(chat);
			setEnableConsultantMode(chat.isConsultantNeeded);
			setIsLoadingChatFirstTime(true);
			isInitialFetchDoneRef.current = false;
			setMessageError(ERROR_INITIAL_STATES);
			setMessage('');
			setIsFileSelected(false);
			setFile(null);
		}

		isCurrentChatNumberRef.current = chat.phoneNumber;
		onResetChatMsgCount(chat.phoneNumber, orgId ? orgId : '');
		setAssignedPackageName(chat.package);

		const data: Array<ChatsResponse> = await fetchChats(
			chat.phoneNumber,
			userId ? userId : '',
			orgId ? orgId : '',
		);

		const updatedData: Array<Chats> = data.map(item => {
			const [type, msg] = Object.entries(item)[1];
			const fileData = item.file ? JSON.parse(msg) : '';
			const itemFileName =
				fileData !== '' &&
				(Object.prototype.hasOwnProperty.call(fileData, 'file_name') ||
					Object.prototype.hasOwnProperty.call(fileData, 'filename'))
					? fileData.filename || fileData.file_name
					: fileData !== '' &&
						  Object.prototype.hasOwnProperty.call(fileData, 'type')
						? fileData.type.startsWith('image')
							? 'Image File'
							: 'Pdf File'
						: undefined;

			return {
				message: item.file ? '' : msg,
				name: chat.phoneNumber,
				timestamp: item.time_stamp,
				type: type,
				fileName: itemFileName,
				fileUrl: item.file
					? `${itemFileName}+${fileData.media_id}+${fileData.type}`
					: undefined,
				caption: item.file ? fileData.caption : '',
				status: item.seen_status,
			};
		});

		const filteredData: Array<Chats> = _.filter(
			updatedData,
			item => _.get(item, 'name') === chat.phoneNumber,
		);

		const chatMessages: Array<ChatData> = filteredData.map(item => {
			return {
				type: item.type,
				text: item.message,
				time: convertTo12HourTime(item.timestamp.split(' ')[1]),
				date: item.timestamp.split(' ')[0],
				fileName: item.fileName,
				fileUrl: item.fileUrl,
				caption: item.caption,
				status: item.status,
			};
		});

		const groupByDate = _.groupBy(chatMessages, 'date');

		// Order Object By Date
		const entries = _.toPairs(groupByDate);
		const sortedEntries = _.orderBy(
			entries,
			[entry => new Date(entry[0])],
			['asc'],
		);
		const sortedObj = _.fromPairs(sortedEntries);

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this if statement (do not remove the code inside the if statement just remove the statement)
		if (isViewClosed) {
			setIsChatWindow(true);
			setIsChatLoading(false);
			setTimeout(() => setIsLoadingChatFirstTime(false), 1000);
		} else {
			setIsLoadingChatFirstTime(false);
		}

		if (
			!isNewMessageSendRef.current &&
			isInitialFetchDoneRef.current &&
			previousChatDataRef.current
		) {
			const previousData = previousChatDataRef.current;
			const newMessages = getNewMessages(previousData, sortedObj);

			if (newMessages.length > 0) {
				setSelectedChatData(prevData => {
					const updatedData = { ...prevData };
					newMessages.forEach(msg => {
						const dateKey = msg.date;
						if (!updatedData[dateKey]) {
							updatedData[dateKey] = [];
						}
						updatedData[dateKey].push(msg);
					});
					return updatedData;
				});
				setIsNewMessageReceived(true);
			} else {
				setSelectedChatData(sortedObj);
				isNewMessageSendRef.current = false;
			}
		} else {
			setSelectedChatData(sortedObj);
			isNewMessageSendRef.current = false;
		}

		previousChatDataRef.current = sortedObj;

		if (!isInitialFetchDoneRef.current) {
			isInitialFetchDoneRef.current = true; // Mark initial fetch as done
		}

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (timeoutId.current) {
			clearTimeout(timeoutId.current);
		}

		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		timeoutId.current = setTimeout(
			() => onViewRecentChat(chat, false),
			10000,
		);
	}

	function normalizeMessage(msg: ChatData) {
		return {
			...msg,
			text: msg.text.trim().toLowerCase(),
			time: msg.time.trim(),
		};
	}

	function getNewMessages(
		previousData: { [index: string]: Array<ChatData> },
		newData: { [index: string]: Array<ChatData> },
	): ChatData[] {
		const newMessages: ChatData[] = [];

		Object.keys(newData).forEach(date => {
			if (!previousData[date]) {
				newMessages.push(...newData[date]);
			} else {
				newData[date].forEach(newMsg => {
					const normalizedNewMsg = normalizeMessage(newMsg);

					const isNewMessage = !previousData[date].some(prevMsg => {
						const normalizedPrevMsg = normalizeMessage(prevMsg);
						return (
							normalizedPrevMsg.time === normalizedNewMsg.time &&
							normalizedPrevMsg.text === normalizedNewMsg.text
						);
					});

					if (isNewMessage) {
						newMessages.push(newMsg);
					}
				});
			}
		});

		return newMessages;
	}

	function onSearchChatList(search: string): void {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		//We are stopping the timeout to show the search results once the search term is empty it will timeout will resume
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}

		setSearchTerm(search);
		const numberRegex = /\d/;
		const searchTag = numberRegex.test(search) ? 'phoneNumber' : 'name';

		const filteredData =
			selectedTag === 'Escalated'
				? chatListAllData.filter(val => val.isConsultantNeeded)
				: chatListAllData;

		if (search === '') {
			setChatListData(filteredData);
		} else {
			const searchResults: Array<ChatList> = filteredData.filter(
				value => {
					return value[searchTag]
						.toLowerCase()
						.match(search.toLowerCase());
				},
			);

			if (searchResults.length > 0) {
				setChatListData(searchResults);
			} else {
				setChatListData([]);
			}
		}
	}

	function handleMarkMessagesRead() {
		enableAutoScrollRef.current = true;
		setIsNewMessageReceived(false);
		setTimeout(() => {
			enableAutoScrollRef.current = false;
		}, 1000);
	}

	function onChangeGridVisibility() {
		setShowNumberList(!showNumberList);
	}

	async function onHandelConsultantMode(
		event: React.ChangeEvent<HTMLInputElement>,
	) {
		const response = await onEnableAgentMode(
			selectedChat.phoneNumber,
			event.target.checked ? 'on' : 'off',
			orgId ? orgId : '',
		);

		if (!response.error) {
			setEnableConsultantMode(!event.target.checked);
			fetchChatNumbersList(false);
			setMessage('');
			setIsFileSelected(false);
			setMessageError(ERROR_INITIAL_STATES);
		}

		onAddClickStats(
			`Consultant mode ${event.target.checked ? 'on' : 'off'} button`,
		);
	}

	function onFilterNumberUsingTag(tag: string) {
		//TODO: This is a short term fix for real-time update on chat conversation
		//Once it's integrated with actual solution remove this code
		if (numberListTimeoutId.current) {
			clearTimeout(numberListTimeoutId.current);
		}
		setSelectedTag(tag);

		if (tag === 'All') {
			setChatListData(chatListAllData);
		} else if (tag === 'Escalated') {
			const clone = chatListAllData.filter(
				item => item.isConsultantNeeded,
			);
			setChatListData(clone);
		} else {
			setChatListData(chatListAllData);
		}

		if (searchTerm !== '') {
			setSearchTerm('');
		}
	}

	function onChangeMessage(value: string) {
		setMessage(value);
		setMessageError(ERROR_INITIAL_STATES);
	}

	async function onSendMessage() {
		if (isSending) {
			return;
		}

		try {
			const response = await sendMessage(
				selectedChat.phoneNumber,
				message,
				orgId ? orgId : '',
			);

			if (!response.error) {
				const currentDate = new Date();
				const todayDate = currentDate.toISOString().split('T')[0];
				const newMessage: ChatData = {
					type: 'consultant',
					text: message,
					time: currentDate.toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
					date: todayDate,
					status: null,
				};

				const isDateExist =
					Object.keys(selectedChatData).includes(todayDate);
				let clone;
				if (isDateExist) {
					clone = {
						...selectedChatData,
						[todayDate]: [
							...selectedChatData[todayDate],
							newMessage,
						],
					};
				} else {
					clone = {
						...selectedChatData,
						[todayDate]: [newMessage],
					};
				}

				setSelectedChatData(clone);
				setMessage('');
				setIsLoadingChatFirstTime(true);
				isNewMessageSendRef.current = true;
				fetchMsgDetails();
			} else {
				setMessageError({ status: true, msg: 'Something went wrong' });
			}
		} catch (error) {
			setMessageError({ status: true, msg: 'Failed to send message' });
		} finally {
			setIsSending(false);
		}
	}

	async function onSendUploadedFile(uploadingFile: File) {
		if (isSending) {
			return;
		}

		try {
			const uploadData = await onFetchUploadInfo();
			const response = await uploadPdf(
				uploadingFile,
				uploadData.version,
				uploadData.phone_id,
				uploadData.token,
				userId ? userId : '',
				orgId ? orgId : '',
			);

			if (!response.error) {
				const uploadResponse = await sendUploadIdAndMessage(
					response.data.id,
					selectedChat.phoneNumber,
					message,
					uploadingFile.name,
					orgId ? orgId : '',
				);

				if (!uploadResponse.error) {
					const currentDate = new Date();
					const todayDate = currentDate.toISOString().split('T')[0];
					const newMessage: ChatData = {
						type: 'consultant',
						text: '',
						time: currentDate.toLocaleTimeString([], {
							hour: '2-digit',
							minute: '2-digit',
						}),
						date: todayDate,
						fileName: uploadingFile.name,
						fileUrl: `${uploadingFile.name}+${response.data.id}`,
						caption: message,
						status: null,
					};

					const isDateExist =
						Object.keys(selectedChatData).includes(todayDate);
					let clone;
					if (isDateExist) {
						clone = {
							...selectedChatData,
							[todayDate]: [
								...selectedChatData[todayDate],
								newMessage,
							],
						};
					} else {
						clone = {
							...selectedChatData,
							[todayDate]: [newMessage],
						};
					}

					setSelectedChatData(clone);
					setFile(null);
					setMessage('');
					setIsFileSelected(false);
					setPreviewUrl('');
					setIsLoadingChatFirstTime(true);
					isNewMessageSendRef.current = true;
				} else {
					setMessageError({
						status: true,
						msg: 'Error sending the file',
					});
				}
			} else {
				setMessageError({
					status: true,
					msg: 'Error sending the file',
				});
			}
		} catch (error) {
			setMessageError({ status: true, msg: 'Failed to send message' });
		} finally {
			setIsSending(false);
		}
	}

	async function onSendChat() {
		if (isFileSelected && file) {
			setIsSending(true);
			onSendUploadedFile(file);
		} else {
			if (!message || message === '') {
				return;
			}
			setIsSending(true);
			onSendMessage();
		}
	}

	function onCancelSelectedFile() {
		setIsFileSelected(false);
		setFile(null);
		setMessageError(ERROR_INITIAL_STATES);
	}

	function onSelectAttachments(event: React.ChangeEvent<HTMLInputElement>) {
		const selectedFile = event.target.files ? event.target.files[0] : null;

		if (selectedFile) {
			if (
				selectedFile.type !== 'application/pdf' &&
				selectedFile.type !== 'image/png' &&
				selectedFile.type !== 'image/jpeg'
			) {
				setMessageError({
					status: true,
					msg: 'Invalid file format. only png, jpeg and pdf files are supported',
				});
				return;
			}

			setFile(selectedFile);
			setIsFileSelected(true);
			setMessageError(ERROR_INITIAL_STATES);
			if (selectedFile.type.startsWith('image')) {
				const preview = URL.createObjectURL(selectedFile);
				setPreviewUrl(preview);
			} else {
				setPreviewUrl('');
			}

			if (event.target.value) {
				event.target.value = '';
			}
		}
	}

	async function onDownloadFileWithUrl(url: string) {
		setIsDownloading(true);
		setDownloadingFile(url);
		const downloadData = url.split('+');
		const response = await downloadMediaFile(
			downloadData[1],
			userId ? userId : '',
			orgId ? orgId : '',
		);

		if (response.error) {
			console.error(
				'Error downloading the media file:',
				response.message,
			);
			return;
		}

		const blobData = response.data;
		const contentType =
			downloadData[2] !== '' ? downloadData[2] : 'application/pdf';
		const blob = new Blob([blobData], { type: contentType });
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(blob);
		downloadLink.download = downloadData[0];
		downloadLink.click();
		URL.revokeObjectURL(downloadLink.href);
		setIsDownloading(false);
		setDownloadingFile('');
	}

	const handleScroll = throttle(() => {
		if (messagesEndRef.current) {
			const isAtBottom =
				messagesEndRef.current.scrollTop +
					messagesEndRef.current.clientHeight >=
				messagesEndRef.current.scrollHeight - 1;
			if (isAtBottom) {
				setIsNewMessageReceived(false);
			}
		}
	}, 100);

	function onAddClickStats(item: string) {
		onAddClickStat(
			userId ? userId : '',
			item,
			true,
			'Conversations',
			getUserEmail() ? getUserEmail() : 'N/A',
			'N/A',
		);
	}

	function renderBodyContent() {
		if (isMobile) {
			if (showNumberList) {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onViewRecentChat}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							tagList={tagList}
							onSelectTag={onFilterNumberUsingTag}
							selectedTag={selectedTag}
							searchedText={searchTerm}
							isMobile={isMobile}
						/>
					</Grid2>
				);
			} else {
				return (
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={2}
						height={'85vh'}
						rowSpacing={{ xs: 2 }}>
						<ForwardedChatWindowMain
							isChatWindow={isChatWindow}
							selectedChatData={selectedChatData}
							ref={messagesEndRef}
							isChatLoading={isChatLoading}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							message={message}
							setMessage={onChangeMessage}
							isConsultantModeOn={enableConsultantMode}
							onChangeConsultantMode={onHandelConsultantMode}
							selectedChat={selectedChat}
							onSendChat={onSendChat}
							messageError={messageError}
							isFileSelected={isFileSelected}
							selectedFile={file}
							onCancelSelectedFile={onCancelSelectedFile}
							onSelectAttachments={onSelectAttachments}
							previewUrl={previewUrl}
							onDownloadFile={onDownloadFileWithUrl}
							isNewMessageReceived={isNewMessageReceived}
							onScrollDown={handleMarkMessagesRead}
							isInitialFetchDone={isInitialFetchDoneRef.current}
							handleScroll={handleScroll}
							isSending={isSending}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							packageName={assignedPackageName}
						/>
					</Grid2>
				);
			}
		} else {
			return (
				<Grid2
					container
					paddingLeft={'20px'}
					paddingRight={'20px'}
					flexDirection={'column'}>
					<Grid2
						container
						marginLeft={'10px'}
						columnSpacing={1}
						height={'85vh'}
						rowSpacing={{ xs: 1 }}>
						<ChatNumbersList
							chatListData={chatListData}
							onSearchChatList={onSearchChatList}
							onViewRecentChat={onViewRecentChat}
							selectedPhoneNumber={selectedChat.phoneNumber}
							loading={loading}
							onChangeGridVisibility={onChangeGridVisibility}
							tagList={tagList}
							onSelectTag={onFilterNumberUsingTag}
							selectedTag={selectedTag}
							searchedText={searchTerm}
						/>
						<ForwardedChatWindowMain
							isChatWindow={isChatWindow}
							selectedChatData={selectedChatData}
							ref={messagesEndRef}
							isChatLoading={isChatLoading}
							isMobile={isMobile}
							onclickGoBack={onChangeGridVisibility}
							message={message}
							setMessage={onChangeMessage}
							isConsultantModeOn={enableConsultantMode}
							onChangeConsultantMode={onHandelConsultantMode}
							selectedChat={selectedChat}
							onSendChat={onSendChat}
							messageError={messageError}
							isFileSelected={isFileSelected}
							selectedFile={file}
							onCancelSelectedFile={onCancelSelectedFile}
							onSelectAttachments={onSelectAttachments}
							previewUrl={previewUrl}
							onDownloadFile={onDownloadFileWithUrl}
							isNewMessageReceived={isNewMessageReceived}
							onScrollDown={handleMarkMessagesRead}
							isInitialFetchDone={isInitialFetchDoneRef.current}
							handleScroll={handleScroll}
							isSending={isSending}
							isDownloading={isDownloading}
							downloadingFile={downloadingFile}
							packageName={assignedPackageName}
						/>
					</Grid2>
				</Grid2>
			);
		}
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>{renderBodyContent()}</div>
		</>
	);
}

export default Conversations;
